// src/components/CompletedTrainingModal/CompletedTrainingModal.jsx
import React, { useState } from 'react'
import { X, Star, AlertCircle, CalendarCheck, Timer, Play, Check, MessageCircle } from 'lucide-react'
import '../FeedbackModal/FeedbackModal.css'  // reuse your existing modal CSS
import "./CompletedTrainingModal.css"
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux';
import { triggerReset } from '../../store/meetingRoomSlice'
import { formatElapsed } from '../../utils/formatTime'
import useAnimatedCount from '../../hooks/useAnimatedCount'
import { canUseTrainingPlans } from '../../utils/billingPlans'
import StreakCompletionStep from '../Streak/StreakCompletionStep'

export default function CompletedTrainingModal({ plan, meeting, streak, onClose }) {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user?.data);
  const planCode = user?.billing?.plan?.code || user?.entitlements?.planCode || '';
  const planLabel = user?.billing?.plan?.name || user?.entitlements?.planName || '';
  const trainingPlansEnabled = canUseTrainingPlans(planCode, planLabel);
  const [showStreak, setShowStreak] = useState(Boolean(streak));

  const completedAt = plan.completed_at || plan.training_complete_time;
  const completedAtFormatted = completedAt
    ? new Date(completedAt).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    : null;

  const animatedScore = useAnimatedCount(plan.score);
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-content modal-content-lg completed-training-modal">

        {/* ✅ HERO ROW (same as FeedbackModal) */}
        <div className="modal-hero-row">
          <div className="modal-hero__center">
            <h2 className="modal-hero__title">
              {showStreak ? 'Session Complete' : 'Training Complete'}
            </h2>
          </div>

          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        {/* ✅ SCROLLABLE BODY (same as FeedbackModal) */}
        <div className="modal-scrollable">
          {showStreak ? (
            <StreakCompletionStep
              streak={streak}
              onContinue={() => setShowStreak(false)}
            />
          ) : (
            <>
              {/* Score row (reuse score-badge styling) */}
              <div className="feedback-header">
                <div className="score-badge">
                  <Star className="score-icon" size={22} fill="#fcb931" color="#fcb931" />
                  <span id="score-number">{animatedScore}/10</span>
                </div>
              </div>

              {/* Title row + status chip */}
              <div className="completed-training-plan-header">
                <h3 className="completed-training-title">
                  {plan.title}
                  <span className="status-Complete completed-status-badge">
                    <span className="completed-status-badge__icon" aria-hidden="true">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>Completed</span>
                  </span>
                </h3>

                {completedAtFormatted && (
                  <div className="completed-at-row">
                    <CalendarCheck size={20} />
                    <span className="completed-at-text">Completed on {completedAtFormatted}</span>
                  </div>
                )}

                {typeof plan.timeSpentInSeconds === "number" && (
                  <div className="completed-time-row">
                    <Timer size={18} />
                    <span>{formatElapsed(plan.timeSpentInSeconds)} / {plan.duration || "N/A"} min</span>
                  </div>
                )}
              </div>

              {/* ✅ Feedback summary block (use SAME classes as FeedbackModal) */}
              <div className="modal-section modal-section-animated feedback-summary">
                <h4 className="section-header">
                  <MessageCircle size={20} color="#72b4fe" fill="#72b4fe" />
                  Training Summary
                </h4>
                <p className="summary-feedback-text">{plan.trainingFeedback}</p>
              </div>

              {/* ✅ Strengths block (same as FeedbackModal) */}
              {plan.strengths?.length > 0 && (
                <div className="modal-section modal-section-animated strength-section">
                  <h4 className="section-header">
                    <Check size={20} color="#017d2a" fill="#b1f0c5" />
                    Strengths
                  </h4>
                  <ul className="feedback-section-list">
                    {plan.strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}

              {/* ✅ Improvements block (use weakness-section to match styling) */}
              {plan.areasForImprovement?.length > 0 && (
                <div className="modal-section modal-section-animated weakness-section">
                  <h4 className="section-header">
                    <AlertCircle size={20} color="#9a5202" fill="#febe4e" />
                    Areas to Improve
                  </h4>
                  <ul className="feedback-section-list">
                    {plan.areasForImprovement.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* ✅ FOOTER (same as FeedbackModal) */}
        {!showStreak ? (
          <div className="button-actions-row">
            <button className="button button-outline" onClick={onClose}>
              Close
            </button>

            <button
              className="button button-primary training-button"
              onClick={() => {
                onClose();
                if (!trainingPlansEnabled) {
                  navigate('/pricing');
                  return;
                }
                dispatch(triggerReset());
                navigate(`/meeting/${meeting.id}`, {
                  state: { meetingData: meeting, trainingPlanId: plan.id }
                });
              }}
            >
              {trainingPlansEnabled ? 'Practice' : 'Upgrade'}
              <Play size={18} />
            </button>
          </div>
        ) : null}

      </div>
    </div>
  );

}
